/**
 * Benchmark Studio API client.
 *
 * Wraps the TAGP backend routes under ``/api/v1/benchmark-studio`` and the
 * Agentserve helper routes used by the Conversation Browser.
 */

import { apiErrorFromResponseBody, request, ApiError } from './core'
import { config } from '../../config'
import { createServiceFetch } from '../../utils/validateFetchUrl'

const benchmarkFetch = createServiceFetch(config.api.baseUrl)
// Some benchmark-studio reads come from Agentserve (/agentserve/api/v1/...), a different base.
const benchmarkAgentserveFetch = createServiceFetch(config.api.agentserveUrl)
import { buildQueryString } from '../../utils/apiQueryBuilder'
import { randomUUID } from '../../utils/secureRandom'
import { logger } from '../../utils/logger'

// ── DTOs ───────────────────────────────────────────────────────────────

export type BenchmarkJobStatus =
  | 'draft'
  | 'collecting'
  | 'annotating'
  | 'adjudicating'
  | 'snapshotting'
  | 'closed'

export interface BenchmarkJob {
  id: string
  tenant_id: string
  tenant_name?: string | null
  project_id?: string | null
  agent_id: string
  agent_name?: string | null
  target_registered_table_id?: string | null
  target_registered_table_name?: string | null
  rubric_id?: string | null
  name: string
  description?: string | null
  benchmark_type?: string | null
  status: BenchmarkJobStatus
  settings_json?: Record<string, unknown> | null
  created_by?: string | null
  creator_name?: string | null
  created_at: number
  updated_at: number
}

export interface JobList {
  items: BenchmarkJob[]
  total: number
}

export interface JobCreatePayload {
  name: string
  description?: string
  project_id?: string
  agent_id: string
  benchmark_type?: string
  target_registered_table_id?: string
  rubric_id?: string
  settings_json?: Record<string, unknown>
}

export interface JobUpdatePayload {
  name?: string
  description?: string
  benchmark_type?: string
  target_registered_table_id?: string
  rubric_id?: string
  status?: BenchmarkJobStatus
  settings_json?: Record<string, unknown>
}

export interface BenchmarkTableColumnPayload {
  name: string
  type: string
  nullable?: boolean
  primary_key?: boolean
  default?: string
}

export interface BenchmarkTableCreatePayload {
  connector_connection_id: string
  database?: string
  schema?: string
  table: string
  display_name?: string
  benchmark_type?: string
  columns: BenchmarkTableColumnPayload[]
}

export interface BenchmarkTableCreateResult {
  registered_table_id: string
  display_name: string
  database: string
  schema: string
  table: string
  connector_type: string
  benchmark_type?: string | null
}

export interface BenchmarkTask {
  id: string
  job_id: string
  tenant_id: string
  session_id: string
  agent_id: string
  agent_version?: string | null
  environment?: string | null
  user_message_id?: string | null
  assistant_message_id: string
  trace_id?: string | null
  raw_input?: string | null
  raw_expected_output?: string | null
  curated_input?: string | null
  curated_output?: string | null
  tool_trace_json?: Record<string, unknown> | null
  status: string
  priority: number
  is_golden: boolean
  tags_json?: string[]
  assignee_user_ids?: string[]
  assignee_names?: Record<string, string>
  created_at: number
  updated_at: number
}

export interface TaskList {
  items: BenchmarkTask[]
  total: number
}

export interface TaskIngestRow {
  session_id: string
  user_message_id?: string | null
  assistant_message_id: string
  trace_id?: string | null
  environment?: string | null
  agent_version?: string | null
  agent_id?: string | null
  raw_input?: string | null
  raw_expected_output?: string | null
  tool_trace_json?: Record<string, unknown> | null
  priority?: number
  is_golden?: boolean
  golden_truth_json?: Record<string, unknown> | null
}

export interface TaskIngestResponse {
  inserted: number
  skipped_duplicates: number
  task_ids: string[]
}

export interface BenchmarkAnnotation {
  id: string
  task_id: string
  annotator_user_id?: string | null
  edited_input?: string | null
  edited_expected_output?: string | null
  edited_tool_trace_json?: Record<string, unknown> | null
  ratings_json?: Record<string, unknown> | null
  labels_json?: string[] | null
  rationale?: string | null
  redactions_json?: Array<Record<string, unknown>> | null
  decision: 'approve' | 'reject' | 'needs_more_info'
  confidence?: number | null
  time_spent_ms?: number | null
  is_adjudication: boolean
  created_at: number
}

export interface AnnotationUpsertPayload {
  edited_input?: string
  edited_expected_output?: string
  edited_tool_trace_json?: Record<string, unknown>
  ratings_json?: Record<string, unknown>
  labels_json?: string[]
  rationale?: string
  redactions_json?: Array<Record<string, unknown>>
  decision?: 'approve' | 'reject' | 'needs_more_info'
  confidence?: number
  time_spent_ms?: number
}

export interface AssignmentCreatePayload {
  annotator_user_ids: string[]
  due_at?: number
}

export interface AssignmentRead {
  id: string
  task_id: string
  annotator_user_id?: string | null
  assigned_by?: string | null
  assigned_at: number
  due_at?: number | null
  completed_at?: number | null
}

export interface TaskDetail {
  task: BenchmarkTask
  annotations: BenchmarkAnnotation[]
}

export interface SnapshotRead {
  id: string
  job_id: string
  tenant_id: string
  target_registered_table_id?: string | null
  version: number
  row_count: number
  sha256?: string | null
  manifest_json?: Record<string, unknown> | null
  exported_paths_json?: Record<string, unknown> | null
  created_by?: string | null
  created_at: number
}

export interface RubricCriterion {
  key: string
  label: string
  anchors?: Record<string, string>
  weight?: number
}

export interface RubricSchema {
  scale: { type: string; min: number; max: number }
  criteria: RubricCriterion[]
  labels: string[]
}

export interface RubricRead {
  id: string
  tenant_id: string
  name: string
  version: number
  description?: string | null
  schema_json: RubricSchema
  created_at: number
  updated_at: number
}

export interface RubricCreatePayload {
  name: string
  description?: string
  schema_json: RubricSchema
}

export interface JobMetrics {
  pending: number
  in_review: number
  adjudication: number
  approved: number
  rejected: number
  exported: number
  golden_count: number
  ground_truth_complete?: number
  ground_truth_pending?: number
  annotators: string[]
  kappa_per_criterion: Record<string, number>
  alpha_per_criterion: Record<string, number>
  golden_pass_rate: Record<string, number>
  contributions?: ContributorStats[]
}

export interface ContributorStats {
  user_id: string
  display_name?: string | null
  contributions: number
  rows_touched: number
  updates_count: number
  last_update_at?: number | null
  domain_tags: string[]
  assigned_count: number
  completed_assignments: number
  ground_truths_created?: number
  answers_modified?: number
  avg_time_ms?: number | null
  incomplete_assignments?: number
}

export interface ContributionRowSample {
  annotator_user_id?: string | null
  job_id: string
  job_name?: string | null
  task_id: string
  question_preview?: string | null
  golden_answer_preview?: string | null
  tags: string[]
  updated_at?: number | null
}

export interface VelocityBucket {
  period: string
  ground_truths_set: number
  tasks_completed: number
  annotations_submitted: number
}

export interface StatusBreakdown {
  unassigned: number
  pending: number
  in_review: number
  adjudication: number
  approved: number
  rejected: number
  exported: number
  golden: number
}

export interface TagCoverage {
  tag: string
  count: number
  ground_truth_complete: number
}

export interface StalledAssignment {
  assignment_id: string
  task_id: string
  job_id: string
  job_name?: string | null
  annotator_user_id: string
  annotator_display_name?: string | null
  assigned_at: number
  hours_stalled: number
  question_preview?: string | null
}

export interface PromotionCandidate {
  task_id: string
  job_id: string
  job_name?: string | null
  annotator_count: number
  agreement: boolean
  question_preview?: string | null
  proposed_answer_preview?: string | null
  proposed_decision?: string | null
  tags: string[]
  is_golden: boolean
}

export interface AuditTrailEntry {
  event_type: string
  task_id?: string | null
  job_id?: string | null
  job_name?: string | null
  actor_user_id?: string | null
  actor_display_name?: string | null
  previous_answer_preview?: string | null
  new_answer_preview?: string | null
  created_at: number
  notes?: string | null
}

export interface AnnotatorOption {
  user_id: string
  display_name?: string | null
}

export interface TenantContributions {
  ground_truth_complete: number
  ground_truth_pending: number
  total_rows: number
  total_updates: number
  golden_count?: number
  golden_set_pct?: number
  users: ContributorStats[]
  users_total?: number
  users_page?: number
  users_page_size?: number
  samples: ContributionRowSample[]
  velocity?: VelocityBucket[]
  status_breakdown?: StatusBreakdown
  tag_coverage?: TagCoverage[]
  stalled?: StalledAssignment[]
  stalled_total?: number
  stalled_page?: number
  stalled_page_size?: number
  promotion_candidates?: PromotionCandidate[]
  promotion_candidates_total?: number
  promotion_page?: number
  promotion_page_size?: number
  audit_trail?: AuditTrailEntry[]
  audit_trail_total?: number
  audit_page?: number
  audit_page_size?: number
  annotator_options?: AnnotatorOption[]
  kappa_per_criterion?: Record<string, number>
  alpha_per_criterion?: Record<string, number>
  decision_agreement_rate?: number | null
  answer_agreement_rate?: number | null
  stalled_threshold_hours?: number
}

export type TenantAnalytics = TenantContributions

export interface AnnotationReminderResult {
  sent: number
  failed: number
  skipped_no_email: number
  skipped_no_pending: number
}

export interface BulkReassignResult {
  unassigned: number
  assigned: number
  skipped: number
}

export interface PromoteGoldenResult {
  promoted: number
  skipped: number
}

export interface BenchmarkRowVersion {
  id: string
  registered_table_id: string
  row_key: string
  version: number
  payload_json: Record<string, unknown>
  changed_fields_json?: string[] | null
  edited_by?: string | null
  created_at: number
  task_id?: string | null
  snapshot_id?: string | null
  notes?: string | null
}

export interface BulkAssignmentResult {
  created: AssignmentRead[]
  skipped: number
}

// ── Agentserve helpers (sessions + turns) ──────────────────────────────

export interface ConversationTurn {
  user_message: {
    message_id: string
    role: string
    content: string
    created_at: number
  } | null
  assistant_message: {
    message_id: string
    role: string
    content: string
    created_at: number
    reasoning_steps?: unknown
    tool_calls?: unknown
    tokens_used?: number | null
    latency_ms?: number | null
    trace_id?: string | null
  }
  trace_id?: string | null
  created_at: number
  /** Present when `job_id` was passed to GET /turns. */
  already_imported?: boolean
  task_id?: string | null
  task_status?: string | null
}

export interface ConversationTurnsResponse {
  turns: ConversationTurn[]
  session_id: string
  agent_id: string
  agent_version?: string | null
  environment?: string | null
  /** True when older raw messages exist (pass `before` to page backward). */
  has_more: boolean
}

const AGENTSERVE_BASE = `${config.api.agentserveUrl ?? ''}/api/v1`
const TAGP_BASE = '/v1/benchmark-studio'
const PORTAL_BASE = '/v1/benchmark-studio/portal'

async function portalRequest<T>(path: string, token: string, options: RequestInit = {}): Promise<T> {
  const url = `${config.api.baseUrl}${path}`
  const res = await benchmarkFetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'X-Benchmark-Token': token,
      ...options.headers,
    },
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    throw apiErrorFromResponseBody(res.status, body, 'Portal request failed')
  }
  if (res.status === 204) return undefined as T
  const text = await res.text()
  if (!text.trim()) return undefined as T
  return JSON.parse(text) as T
}

function agentserveAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  try {
    const token = localStorage.getItem(config.auth.tokenKey)
    if (token) headers.Authorization = `Bearer ${token}`
  } catch {
    /* ignore */
  }
  try {
    const tenantId = localStorage.getItem('tagp-tenant-id')
    if (tenantId) headers['X-Tenant-ID'] = tenantId
  } catch {
    /* ignore */
  }
  return headers
}

async function agentserveGet<T>(path: string): Promise<T> {
  const res = await benchmarkAgentserveFetch(`${AGENTSERVE_BASE}${path}`, {
    method: 'GET',
    headers: agentserveAuthHeaders(),
  })
  if (!res.ok) {
    const detail = await res.text().catch(() => '')
    throw new Error(`Agentserve ${res.status}: ${detail || res.statusText}`)
  }
  return res.json() as Promise<T>
}

export const benchmarkStudioApi = {
  createBenchmarkTable: (payload: BenchmarkTableCreatePayload, options?: { tenantId?: string }) =>
    request<BenchmarkTableCreateResult>(`${TAGP_BASE}/benchmark-tables`, {
      method: 'POST',
      body: JSON.stringify(payload),
      tenantId: options?.tenantId,
    }),

  // ── Jobs ────────────────────────────────────────────────────────────
  createJob: (payload: JobCreatePayload) =>
    request<BenchmarkJob>(`${TAGP_BASE}/jobs`, { method: 'POST', body: JSON.stringify(payload) }),

  listJobs: (params?: {
    project_id?: string
    status?: BenchmarkJobStatus
    agent_id?: string
    page?: number
    page_size?: number
    scope?: 'all'
  }) => request<JobList>(`${TAGP_BASE}/jobs${buildQueryString(params)}`),

  getJob: (jobId: string) => request<BenchmarkJob>(`${TAGP_BASE}/jobs/${jobId}`),

  updateJob: (jobId: string, payload: JobUpdatePayload) =>
    request<BenchmarkJob>(`${TAGP_BASE}/jobs/${jobId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteJob: (jobId: string) =>
    request<void>(`${TAGP_BASE}/jobs/${jobId}`, { method: 'DELETE' }),

  getJobMetrics: (jobId: string) =>
    request<JobMetrics>(`${TAGP_BASE}/jobs/${jobId}/metrics`),

  // ── Tasks ───────────────────────────────────────────────────────────
  bulkIngestTasks: (jobId: string, rows: TaskIngestRow[], idempotencyKey?: string) => {
    const key =
      idempotencyKey?.trim() ||
      randomUUID()
    return request<TaskIngestResponse>(`${TAGP_BASE}/jobs/${jobId}/tasks:bulk`, {
      method: 'POST',
      body: JSON.stringify({ rows }),
      headers: { 'Idempotency-Key': key },
    })
  },

  listTasks: (
    jobId: string,
    params?: {
      status?: string
      is_golden?: boolean
      assignee?: string
      tag?: string
      mine?: boolean
      page?: number
      page_size?: number
    }
  ) => request<TaskList>(`${TAGP_BASE}/jobs/${jobId}/tasks${buildQueryString(params)}`),

  getTask: (taskId: string) =>
    request<TaskDetail>(`${TAGP_BASE}/tasks/${taskId}`),

  deleteTask: (taskId: string) =>
    request<void>(`${TAGP_BASE}/tasks/${taskId}`, { method: 'DELETE' }),

  upsertAnnotation: (taskId: string, payload: AnnotationUpsertPayload) =>
    request<BenchmarkAnnotation>(`${TAGP_BASE}/tasks/${taskId}/annotations`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  createAssignments: (taskId: string, payload: AssignmentCreatePayload) =>
    request<AssignmentRead[]>(`${TAGP_BASE}/tasks/${taskId}/assignments`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  deleteAssignment: (taskId: string, annotatorUserId: string) =>
    request<void>(`${TAGP_BASE}/tasks/${taskId}/assignments/${annotatorUserId}`, {
      method: 'DELETE',
    }),

  bulkCreateAssignments: (
    jobId: string,
    payload: { task_ids: string[]; annotator_user_ids: string[]; due_at?: number }
  ) =>
    request<BulkAssignmentResult>(`${TAGP_BASE}/jobs/${jobId}/assignments:bulk`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  updateTaskTags: (taskId: string, payload: { tags: string[]; merge?: boolean }) =>
    request<BenchmarkTask>(`${TAGP_BASE}/tasks/${taskId}/tags`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  reopenTask: (taskId: string) =>
    request<BenchmarkTask>(`${TAGP_BASE}/tasks/${taskId}/reopen`, { method: 'POST' }),

  getContributions: (params?: {
    project_id?: string
    job_id?: string
    tag?: string
    user_id?: string
    from?: number
    to?: number
    velocity_grain?: 'daily' | 'weekly' | 'monthly'
    stalled_threshold_hours?: number
    users_page?: number
    users_page_size?: number
    stalled_page?: number
    stalled_page_size?: number
    promotion_page?: number
    promotion_page_size?: number
    audit_page?: number
    audit_page_size?: number
  }) =>
    request<TenantContributions>(`${TAGP_BASE}/contributions${buildQueryString(params)}`),

  getAnalytics: (params?: {
    project_id?: string
    /** Studio session (job) scope — omit for tenant-wide Global view. */
    job_id?: string
    /** Section within a session — task tag filter (requires job_id). */
    tag?: string
    user_id?: string
    from?: number
    to?: number
    velocity_grain?: 'daily' | 'weekly' | 'monthly'
    stalled_threshold_hours?: number
    users_page?: number
    users_page_size?: number
    stalled_page?: number
    stalled_page_size?: number
    promotion_page?: number
    promotion_page_size?: number
    audit_page?: number
    audit_page_size?: number
  }) =>
    request<TenantAnalytics>(`${TAGP_BASE}/analytics${buildQueryString(params)}`),

  sendAnnotationReminders: (payload: {
    annotator_user_ids: string[]
    message?: string
  }) =>
    request<AnnotationReminderResult>(`${TAGP_BASE}/analytics/reminders`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  bulkReassign: (payload: {
    task_ids: string[]
    from_annotator_user_id: string
    to_annotator_user_ids: string[]
    due_at?: number
  }) =>
    request<BulkReassignResult>(`${TAGP_BASE}/analytics/reassign`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  promoteGolden: (payload: { task_ids: string[] }) =>
    request<PromoteGoldenResult>(`${TAGP_BASE}/analytics/promote-golden`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  patchBenchmarkRow: (
    registeredTableId: string,
    rowKey: string,
    payload: {
      expected_output?: string
      input?: string
      labels?: unknown
      tags?: string[]
      notes?: string
    }
  ) =>
    request<BenchmarkRowVersion>(
      `${TAGP_BASE}/benchmark-tables/${registeredTableId}/rows/${encodeURIComponent(rowKey)}`,
      { method: 'PATCH', body: JSON.stringify(payload) }
    ),

  listBenchmarkRowVersions: (registeredTableId: string, rowKey: string) =>
    request<{ items: BenchmarkRowVersion[]; total: number }>(
      `${TAGP_BASE}/benchmark-tables/${registeredTableId}/rows/${encodeURIComponent(rowKey)}/versions`
    ),

  restoreBenchmarkRowVersion: (
    registeredTableId: string,
    rowKey: string,
    version: number
  ) =>
    request<BenchmarkRowVersion>(
      `${TAGP_BASE}/benchmark-tables/${registeredTableId}/rows/${encodeURIComponent(rowKey)}/versions/${version}/restore`,
      { method: 'POST' }
    ),

  adjudicate: (
    taskId: string,
    payload: {
      winning_annotation_id?: string
      edited_input?: string
      edited_expected_output?: string
      edited_tool_trace_json?: Record<string, unknown>
      ratings_json?: Record<string, unknown>
      labels_json?: string[]
      rationale?: string
      decision?: 'approve' | 'reject'
    }
  ) =>
    request<BenchmarkAnnotation>(`${TAGP_BASE}/tasks/${taskId}/adjudicate`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // ── Rubrics ─────────────────────────────────────────────────────────
  createRubric: (payload: RubricCreatePayload) =>
    request<RubricRead>(`${TAGP_BASE}/rubrics`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  listRubrics: (params?: { page?: number; page_size?: number }) =>
    request<{ items: RubricRead[]; total: number }>(
      `${TAGP_BASE}/rubrics${buildQueryString(params)}`
    ),

  getRubric: (rubricId: string) =>
    request<RubricRead>(`${TAGP_BASE}/rubrics/${rubricId}`),

  // ── Goldens ─────────────────────────────────────────────────────────
  markGoldens: (
    jobId: string,
    payload: { task_ids: string[]; truth?: Record<string, unknown>; is_golden?: boolean }
  ) =>
    request<{ updated: number }>(`${TAGP_BASE}/jobs/${jobId}/goldens`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  // ── Snapshot ────────────────────────────────────────────────────────
  createSnapshot: (
    jobId: string,
    payload: { notes?: string; only_task_ids?: string[]; min_golden_pass_rate?: number },
    idempotencyKey?: string
  ) => {
    const key =
      idempotencyKey?.trim() ||
      randomUUID()
    return request<SnapshotRead>(`${TAGP_BASE}/jobs/${jobId}/snapshot`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Idempotency-Key': key },
    })
  },

  getSnapshot: (snapshotId: string) =>
    request<SnapshotRead>(`${TAGP_BASE}/snapshots/${snapshotId}`),

  listJobSnapshots: (
    jobId: string,
    params?: { page?: number; page_size?: number }
  ) =>
    request<{ items: SnapshotRead[]; total: number }>(
      `${TAGP_BASE}/jobs/${jobId}/snapshots${buildQueryString(params)}`
    ),

  // ── Agentserve helpers (Conversation Browser) ───────────────────────
  listAgentSessions: (
    agentId: string,
    params: {
      page?: number
      page_size?: number
      environment?: string
      search?: string
      scope?: 'mine' | 'tenant_visible'
    } = {}
  ) =>
    agentserveGet<{
      items: Array<{
        session_id: string
        agent_id: string
        agent_version?: string | null
        environment?: string | null
        user_id?: string | null
        title?: string | null
        status: string
        visibility?: string | null
        created_at: number
        updated_at: number
      }>
      total: number
      page: number
      page_size: number
    }>(`/sessions${buildQueryString({ agent_id: agentId, ...params })}`),

  getSessionTurns: (
    agentId: string,
    sessionId: string,
    limit = 200,
    before?: string,
    jobId?: string
  ) =>
    agentserveGet<ConversationTurnsResponse>(
      `/agents/${agentId}/sessions/${sessionId}/turns${buildQueryString({
        limit,
        ...(before ? { before } : {}),
        ...(jobId ? { job_id: jobId } : {}),
      })}`
    ),

  // ── Phase 3 — Snapshot diff & re-export ────────────────────────────
  diffSnapshot: (snapshotId: string, otherId?: string) =>
    request<BenchmarkSnapshotDiff>(
      `${TAGP_BASE}/snapshots/${snapshotId}/diff${buildQueryString({ other_id: otherId })}`
    ),

  reexportSnapshot: (snapshotId: string) =>
    request<SnapshotRead>(`${TAGP_BASE}/snapshots/${snapshotId}/reexport`, {
      method: 'POST',
    }),

  // ── Phase 3 — External annotator invites ───────────────────────────
  createInvite: (
    jobId: string,
    payload: {
      email: string
      display_name?: string
      expires_at?: number
      max_tasks?: number
    }
  ) =>
    request<ExternalInviteIssued>(`${TAGP_BASE}/jobs/${jobId}/invites`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  listInvites: (jobId: string) =>
    request<ExternalInviteRead[]>(`${TAGP_BASE}/jobs/${jobId}/invites`),

  revokeInvite: (inviteId: string) =>
    request<ExternalInviteRead>(`${TAGP_BASE}/invites/${inviteId}`, {
      method: 'DELETE',
    }),

  // ── Phase 3 — Payouts ──────────────────────────────────────────────
  computePayouts: (
    jobId: string,
    payload: {
      rate_per_task_cents: number
      approval_bonus_cents?: number
      currency?: string
    }
  ) =>
    request<PayoutRead[]>(`${TAGP_BASE}/jobs/${jobId}/payouts:compute`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  listPayouts: (jobId: string) =>
    request<PayoutRead[]>(`${TAGP_BASE}/jobs/${jobId}/payouts`),

  updatePayout: (
    payoutId: string,
    payload: { status: 'draft' | 'approved' | 'paid' | 'canceled'; external_ref?: string; notes?: string }
  ) =>
    request<PayoutRead>(`${TAGP_BASE}/payouts/${payoutId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  // ── Phase 3 — Calibration ──────────────────────────────────────────
  listCalibrations: (jobId: string) =>
    request<CalibrationRead[]>(`${TAGP_BASE}/jobs/${jobId}/calibrations`),

  // ── Session / Commit aliases (UI renamed job->session, snapshot->commit) ──
  createSession: (payload: JobCreatePayload, options?: { tenantId?: string }) =>
    request<BenchmarkJob>(`${TAGP_BASE}/jobs`, {
      method: 'POST',
      body: JSON.stringify(payload),
      tenantId: options?.tenantId,
    }),

  listSessions: (params?: {
    project_id?: string
    status?: BenchmarkJobStatus
    agent_id?: string
    /** Canonical session type id (text2sql, rag, …) for server-side drill-down. */
    benchmark_type?: string
    page?: number
    page_size?: number
    scope?: 'all'
  }) => request<JobList>(`${TAGP_BASE}/jobs${buildQueryString(params)}`),

  getSession: (sessionId: string) => request<BenchmarkJob>(`${TAGP_BASE}/jobs/${sessionId}`),

  updateSession: (sessionId: string, payload: JobUpdatePayload) =>
    request<BenchmarkJob>(`${TAGP_BASE}/jobs/${sessionId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  deleteSession: (sessionId: string, options?: { tenantId?: string }) =>
    request<void>(`${TAGP_BASE}/jobs/${sessionId}`, { method: 'DELETE', tenantId: options?.tenantId }),

  bulkDeleteSessions: (jobIds: string[], options?: { tenantId?: string }) =>
    request<{ deleted: number; skipped: number; errors: string[] }>(
      `${TAGP_BASE}/jobs/bulk-delete`,
      {
        method: 'POST',
        body: JSON.stringify({ job_ids: jobIds }),
        tenantId: options?.tenantId,
      }
    ),

  getSessionMetrics: (sessionId: string, options?: { tenantId?: string }) =>
    request<JobMetrics>(`${TAGP_BASE}/jobs/${sessionId}/metrics`, { tenantId: options?.tenantId }),

  createCommit: (
    sessionId: string,
    payload: { notes?: string; only_task_ids?: string[]; min_golden_pass_rate?: number },
    idempotencyKey?: string
  ) => {
    const key =
      idempotencyKey?.trim() ||
      randomUUID()
    return request<SnapshotRead>(`${TAGP_BASE}/jobs/${sessionId}/snapshot`, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Idempotency-Key': key },
    })
  },

  getCommit: (commitId: string) =>
    request<SnapshotRead>(`${TAGP_BASE}/snapshots/${commitId}`),

  listSessionCommits: (
    sessionId: string,
    params?: { page?: number; page_size?: number }
  ) =>
    request<{ items: SnapshotRead[]; total: number }>(
      `${TAGP_BASE}/jobs/${sessionId}/snapshots${buildQueryString(params)}`
    ),

  // ── GT Interviewer ─────────────────────────────────────────────────
  interviewQueue: (jobId?: string | null) =>
    request<InterviewQueueResponse>(
      `${TAGP_BASE}/interview/queue${buildQueryString({
        ...(jobId ? { job_id: jobId } : {}),
      })}`
    ),

  interviewTurn: (
    payload: InterviewTurnPayload,
    handlers: InterviewTurnHandlers,
    signal?: AbortSignal
  ) => runInterviewTurn(payload, handlers, signal),
}

// ── Phase 3 DTOs ─────────────────────────────────────────────────────

export interface BenchmarkSnapshotDiff {
  snapshot_id: string
  other_snapshot_id: string | null
  added: Array<Record<string, unknown>>
  removed: Array<Record<string, unknown>>
  changed: Array<{ key: string; field_diffs: Record<string, { old: unknown; new: unknown }> }>
  stats: { added: number; removed: number; changed: number; unchanged: number; key_field?: string }
}

export interface ExternalInviteRead {
  id: string
  job_id: string
  tenant_id: string
  email: string
  display_name?: string | null
  status: 'pending' | 'accepted' | 'revoked' | 'expired'
  expires_at?: number | null
  max_tasks?: number | null
  accepted_at?: number | null
  revoked_at?: number | null
  created_at: number
  updated_at: number
}

export interface ExternalInviteIssued {
  invite: ExternalInviteRead
  token: string
  portal_url?: string | null
}

export interface PayoutRead {
  id: string
  job_id: string
  tenant_id: string
  annotator_user_id?: string | null
  external_invite_id?: string | null
  tasks_annotated: number
  tasks_approved: number
  rate_per_task_cents: number
  bonus_cents: number
  total_cents: number
  currency: string
  status: 'draft' | 'approved' | 'paid' | 'canceled'
  computed_at: number
  paid_at?: number | null
  external_ref?: string | null
  notes?: string | null
  created_at: number
  updated_at: number
}

export interface CalibrationRead {
  id: string
  job_id: string
  tenant_id: string
  annotator_user_id?: string | null
  external_invite_id?: string | null
  attempts: number
  passes: number
  pass_rate: number | null
  certified: boolean
  certified_at?: number | null
  threshold: number | null
  created_at: number
  updated_at: number
}

// ── GT Interviewer DTOs ───────────────────────────────────────────────

export type InterviewActionKind =
  | 'draft'
  | 'approve'
  | 'reject'
  | 'skip'
  | 'set_golden'
  | 'snapshot_offer'
  | 'snapshot_created'
  | 'queue_empty'

export type InterviewClientAction =
  | 'approve'
  | 'reject'
  | 'skip'
  | 'set_golden'
  | 'create_snapshot'

export interface InterviewQueueItem {
  task_id: string
  job_id: string
  job_name?: string | null
  position: number
  total: number
  raw_input?: string | null
  raw_expected_output?: string | null
  status: string
  is_golden: boolean
  priority: number
}

export interface InterviewQueueResponse {
  items: InterviewQueueItem[]
  total: number
  closed_count: number
}

export interface InterviewTranscriptMessage {
  role: 'user' | 'assistant' | 'system'
  content: string
}

export interface InterviewTurnPayload {
  job_id?: string | null
  task_id?: string | null
  user_message?: string
  transcript?: InterviewTranscriptMessage[]
  client_action?: InterviewClientAction | null
  draft_expected_output?: string | null
  draft_rationale?: string | null
  draft_edited_input?: string | null
  snapshot_notes?: string | null
}

export interface InterviewAction {
  kind: InterviewActionKind
  task_id?: string | null
  job_id?: string | null
  expected_output?: string | null
  edited_input?: string | null
  rationale?: string | null
  snapshot_id?: string | null
  message?: string | null
  extras?: Record<string, unknown> | null
}

export interface InterviewTurnHandlers {
  onToken?: (text: string) => void
  onAction?: (action: InterviewAction) => void
  onTaskFocus?: (item: InterviewQueueItem) => void
  onError?: (message: string) => void
  onDone?: (ok: boolean) => void
}

function processInterviewSseLines(
  lines: string[],
  state: { event: string; data: string },
  handlers: InterviewTurnHandlers
) {
  for (const raw of lines) {
    const line = raw.replace(/\r$/, '')
    if (line.startsWith(':')) continue
    if (line === '') {
      const data = state.data
      const eventName = state.event
      state.data = ''
      state.event = ''
      if (!data) continue
      try {
        const parsed = JSON.parse(data) as Record<string, unknown>
        if (eventName === 'token') {
          handlers.onToken?.(String(parsed.text ?? ''))
        } else if (eventName === 'action') {
          handlers.onAction?.(parsed as unknown as InterviewAction)
        } else if (eventName === 'task_focus') {
          handlers.onTaskFocus?.(parsed as unknown as InterviewQueueItem)
        } else if (eventName === 'error') {
          handlers.onError?.(String(parsed.message ?? 'Interview error'))
        } else if (eventName === 'done') {
          handlers.onDone?.(Boolean(parsed.ok))
        }
      } catch {
        logger.warn('Failed to parse GT interview SSE data', { eventName, data })
      }
      continue
    }
    if (line.startsWith('event:')) {
      state.event = line.slice(6).trim()
    } else if (line.startsWith('data:')) {
      const chunk = line.slice(5).startsWith(' ') ? line.slice(6) : line.slice(5)
      state.data = state.data ? `${state.data}\n${chunk}` : chunk
    }
  }
}

async function runInterviewTurn(
  payload: InterviewTurnPayload,
  handlers: InterviewTurnHandlers,
  signal?: AbortSignal
): Promise<void> {
  const { auth: authConfig } = config
  let token: string | null = null
  try {
    token = localStorage.getItem(authConfig.tokenKey)
  } catch {
    token = null
  }

  const endpoint = `${TAGP_BASE}/interview/turn`
  const response = await benchmarkFetch(`${config.api.baseUrl}${endpoint}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(payload),
    credentials: 'include',
    signal,
  })

  if (!response.ok) {
    const bodyText = await response.text().catch(() => '')
    throw apiErrorFromResponseBody(response.status, bodyText, 'Interview turn failed')
  }
  if (!response.body) {
    throw new ApiError('Interview stream has no body', 502)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  // Persist event type across chunk flushes (SSE rule).
  const state = { event: '', data: '' }

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    buffer += decoder.decode(value, { stream: true })
    const parts = buffer.split('\n')
    buffer = parts.pop() ?? ''
    processInterviewSseLines(parts, state, handlers)
  }
  if (buffer.trim()) {
    processInterviewSseLines(buffer.split('\n'), state, handlers)
  }
}

export interface PortalSession {
  invite_id: string
  job_id: string
  tenant_id: string
  email: string
  display_name?: string | null
  status: string
  max_tasks?: number | null
}

/** Tokenized external annotator API — no platform login required. */
export const benchmarkStudioPortalApi = {
  getSession: (token: string) => portalRequest<PortalSession>(`${PORTAL_BASE}/session`, token),

  listTasks: (token: string, limit = 25) =>
    portalRequest<BenchmarkTask[]>(`${PORTAL_BASE}/tasks?limit=${limit}`, token),

  submitAnnotation: (token: string, taskId: string, payload: AnnotationUpsertPayload) =>
    portalRequest<BenchmarkAnnotation>(`${PORTAL_BASE}/tasks/${taskId}/annotations`, token, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
}
